<template>
    <wizard
        no-header
        :loading="loading"
    >
        <template #prepend>
            <risk-header :risk="riskHeader"/>
        </template>

        <x-form ref="midtermAdjustmentForm" v-model="isFormValid" class="wizard-step-form" @submit.prevent="submit">
            <x-container fluid>
                <x-layout class="question-set">
                    <x-flex xs2 offset-xs1>
                        <h3 class="question-set-title">Mid-term Adjustment</h3>
                    </x-flex>

                    <x-flex xs6 class="question-set-questions">
                        <x-container>
                            <x-layout>
                                <x-flex xs6>
                                    <x-text-field
                                        v-model="adjustmentTitle"
                                        class="required"
                                        label="MTA Title"
                                        placeholder=" "
                                        :rules="[
                                            v => ruleMaxCharacters(v, 50, 'MTA Title')
                                        ]"
                                    />
                                </x-flex>

                                <x-flex xs6>
                                    <x-text-field
                                        v-model="endorsementReference"
                                        label="Endorsement reference"
                                        placeholder=" "
                                        maxlength="10"
                                        :rules="[(v) => ruleAlphanumeric(v, 'Endorsement reference')]"
                                        outline
                                    />
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex xs6>
                                    <x-menu
                                        v-model="adjustmentDateInput"
                                        :close-on-content-click="false"
                                        lazy
                                        transition="scale-transition"
                                        offset-y
                                        max-width="300"
                                        full-width
                                    >
                                        <template v-slot:activator="{ on }">
                                            <x-text-field
                                                v-model="adjustmentDate"
                                                data-test="effective_date"
                                                class="required"
                                                label="Effective date"
                                                placeholder=" "
                                                append-icon="event"
                                                readonly
                                                :rules="[v => ruleRequired(v, 'Effective date')]"
                                                v-on="on"
                                            />
                                        </template>

                                        <x-date-picker
                                            v-model="adjustmentDate"
                                            @input="adjustmentDateInput = false"
                                            :min="$moment(declaration.inception_date).format('YYYY-MM-DD')"
                                            :max="$moment(declaration.expiry_date).format('YYYY-MM-DD')"
                                        />
                                    </x-menu>
                                </x-flex>

                                <x-flex xs6>
                                    <x-menu
                                        v-model="settlementDueDateInput"
                                        :close-on-content-click="false"
                                        lazy
                                        transition="scale-transition"
                                        offset-y
                                        max-width="300"
                                        full-width
                                    >
                                        <template v-slot:activator="{ on }">
                                            <x-text-field
                                                v-model="settlementDueDate"
                                                data-test="settlement_due_date"
                                                class="required"
                                                label="Settlement due date"
                                                placeholder=" "
                                                append-icon="event"
                                                readonly
                                                :rules="[v => ruleRequired(v, 'Settlement due date')]"
                                                v-on="on"
                                            />
                                        </template>

                                        <x-date-picker
                                            v-model="settlementDueDate"
                                            @input="settlementDueDateInput = false"
                                        />
                                    </x-menu>
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>

                <x-layout mx-2>
                    <x-flex xs12 class="right">
                        <x-card class="panel mb-4">
                            <x-card-title>
                                <h3>{{ isCaptiveReinsurance ? 'Captive line' : 'Signed lines' }}</h3>
                            </x-card-title>

                            <x-card-text>
                                <x-data-table
                                    class="no-row-hover"
                                    :headers="mtaTableHeaders"
                                    :items="adjustmentLines"
                                    hide-actions
                                >
                                    <template v-slot:no-data>
                                        <p class="error-message text-xs-center">No lines found</p>
                                    </template>

                                    <template v-slot:items="props">
                                        <tr>
                                            <td>
                                                {{ props.item.insurer_company_name }}
                                            </td>

                                            <td>
                                                {{ $options.filters.currency(props.item.line_amount, declaration.currency.code) }} ({{ $options.filters.percent(props.item.line_percent) }})
                                            </td>

                                            <td>
                                                <x-currency-text-field
                                                    v-model="props.item.adjustment_amount"
                                                    class="required"
                                                    placeholder=" "
                                                    :prefix="declaration.currency.code"
                                                    :rules="[
                                                        (v) => ruleRequired(v, 'Adjustment amount'),
                                                        (v) => ruleNumeric(v, 'Adjustment amount', false, true),
                                                    ]"
                                                />
                                            </td>

                                            <td>
                                                <x-textarea
                                                    v-model="props.item.commentary"
                                                    class="required"
                                                    placeholder=" "
                                                    required
                                                    :rules="[
                                                        v => ruleMaxCharacters(v, 300, 'Commentary'),
                                                        v => ruleRequired(v, 'Commentary'),
                                                    ]"
                                                />
                                            </td>
                                        </tr>
                                    </template>
                                </x-data-table>
                            </x-card-text>
                        </x-card>

                        <x-card v-if="isCaptiveReinsurance" class="panel mb-4">
                            <x-card-title>
                                <h3>Reinsured lines</h3>
                            </x-card-title>

                            <x-card-text>
                                <x-data-table
                                    class="no-row-hover"
                                    :headers="mtaTableHeaders"
                                    :items="reinsuredAdjustmentLines"
                                    hide-actions
                                >
                                    <template v-slot:no-data>
                                        <p class="error-message text-xs-center">No lines found</p>
                                    </template>

                                    <template v-slot:items="props">
                                        <tr>
                                            <td>
                                                {{ props.item.insurer_company_name }}
                                            </td>

                                            <td>
                                                {{ $options.filters.currency(props.item.line_amount, declaration.currency.code) }} ({{ $options.filters.percent(props.item.line_percent) }})
                                            </td>

                                            <td>
                                                <x-text-field
                                                    v-model="props.item.adjustment_amount"
                                                    class="required"
                                                    placeholder=" "
                                                    :prefix="declaration.currency.code"
                                                    hide-details
                                                    :rules="[
                                                        (v) =>
                                                            ruleRequired(
                                                                v,
                                                                'Adjustment amount'
                                                            ),
                                                        (v) =>
                                                            ruleNumeric(
                                                                v,
                                                                'Adjustment amount'
                                                            ),
                                                    ]"
                                                />
                                            </td>

                                            <td>
                                                <x-textarea
                                                    v-model="props.item.commentary"
                                                    class="required"
                                                    placeholder=" "
                                                    hide-details
                                                    maxlength="300"
                                                    :rules="[v => ruleMaxCharacters(v, 300, 'Commentary')]"
                                                />
                                            </td>
                                        </tr>
                                    </template>
                                </x-data-table>
                            </x-card-text>
                        </x-card>
                    </x-flex>
                </x-layout>

                <x-layout mx-2 mb-5>
                    <x-flex xs4 class="left">
                        <x-card class="panel">
                            <x-card-text>
                                <data-list v-model="mtaDataList" class="data-list" />
                            </x-card-text>
                        </x-card>
                    </x-flex>

                    <x-flex xs8>
                        <x-card class="panel">
                            <x-card-text>
                                <x-switch
                                    v-model="declaration.pricing_structure"
                                    class="double-switch mt-0 mb-4"
                                    label="Horizontal"
                                    true-value="vertical"
                                    false-value="horizontal"
                                    color="primary"
                                    disabled
                                    hide-details
                                >
                                    <template v-slot:append>Vertical</template>
                                </x-switch>

                                <progress-linear-bar
                                    v-model="percentageWritten"
                                    label="Written"
                                    :end-label="$options.filters.percent(percentageWritten)"
                                    :end-label-color="getEndLabelColor(percentageWritten)"
                                    color="teal"
                                />
                            </x-card-text>
                        </x-card>
                    </x-flex>
                </x-layout>

                <x-layout mx-2 mb-5>
                    <x-flex>
                        <document-list
                            :insurer-list="insurerList"
                            v-if="documentableId"
                            class="mt-3"
                            upload
                            insurer-specific-upload
                            :facility-id="declaration.facility_id"
                            :documentable-id="documentableId"
                            documentable-type="endorsement"
                            @documentChanges="documentChanges($event)"
                            @documentsLoading="documentsLoading = $event"
                        />
                    </x-flex>
                </x-layout>

                <x-layout class="action-set action-set-wide" align-center>
                    <x-flex xs12 class="action-set-actions">
                        <x-container fluid>
                            <x-layout>
                                <x-flex xs6>
                                    <x-btn
                                        color="tertiary"
                                        @click="goToDeclaration"
                                    >
                                        <i class="material-icons mr-2">arrow_back</i>

                                        Go Back
                                    </x-btn>
                                </x-flex>

                                <x-flex xs6 text-xs-right v-if="this.midTermAdjustment.status === 'draft'"> <!-- TODO move statuses in computed -->
                                    <x-btn
                                        class="wide"
                                        color="primary"
                                        :loading="btnLoading"
                                        :disabled="documentsLoading"
                                        @click="saveChanges()"
                                    >
                                        Save Changes

                                        <i v-if="!documentsLoading" class="material-icons ml-2">trending_flat</i>
                                    </x-btn>
                                </x-flex>

                                <x-flex xs6 text-xs-right>
                                    <x-btn
                                        class="wide"
                                        color="primary"
                                        :loading="loading"
                                        :disabled="!isFormValid || isButtonDisabled || documentsLoading"
                                        @click="submit"
                                    >
                                        {{ toggleSubmitBtnText }}

                                        <i v-if="!documentsLoading" class="material-icons ml-2">trending_flat</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>
            </x-container>
        </x-form>

        <modal
            ref="confirmation"
            title="Submit mid-term adjustment"
            confirmation
            action-text="Confirm"
            :action-disabled="this.approvingInsurerIds.length === 0"
        >
            <x-layout mb-3>
                <x-flex>
                    <p v-if="isUpdating">
                        Note, any amendments you make for this insurer, the underwriter(s) will receive a notification of the updated information you provide.
                    </p>
                    <p v-else>
                        You are about to submit a mid-term adjustment for risk {{ declaration.risk_id }}
                        <br>
                        Adjustment date {{ adjustmentDate | formatDate }}
                    </p>
                </x-flex>
            </x-layout>

            <x-layout justify-center>
                <x-flex xs6>
                    <x-select
                        label="Require approval from"
                        :items="insurerList"
                        v-model="approvingInsurerIds"
                        placeholder="Require approval from"
                        append-icon="keyboard_arrow_down"
                        outline
                        item-text="name"
                        item-value="id"
                        multiple
                        hide-details
                        data-test="approvers"
                        :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                    >
                        <template slot="selection" slot-scope="{item, index}">
                            <span v-if="index === 0">{{ numberOfInsurersSelected }}</span>
                        </template>

                        <template slot="prepend-item">
                            <x-list-tile class="buttons-top-tile no-hover">
                                <x-btn @click="selectAllInsurers" color="secondary">Select all</x-btn>
                                <x-btn data-test="deselect_all" @click="deselectAllInsurers" color="tertiary">Deselect all</x-btn>
                            </x-list-tile>
                        </template>
                    </x-select>
                </x-flex>
            </x-layout>
        </modal>

        <modal ref="notUniqueEndorsementReference" title="Error">
            <p>Endorsement reference not unique.</p>
            <p>Please change it.</p>
        </modal>
    </wizard>
</template>

<script>
    import Wizard from '../../components/Broker/UIComponents/Wizard.vue';
    import RiskHeader from '~/components/Shared/Partials/RiskHeader';
    import DocumentList from "~/components/Shared/UIComponents/DocumentList";
    import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
    import DataList from '~/components/Shared/UIComponents/DataList';
    import Modal from "~/components/Shared/UIComponents/Modal";
    import rules from "~/mixins/rules";

    export default {
        name: "BrokerMidTermAdjustmentEntry",
        components: {
            Wizard,
            RiskHeader,
            DocumentList,
            ProgressLinearBar,
            DataList,
            Modal
        },
        mixins: [rules],
        data() {
            return {
                isFormValid: true,
                loading: true,
                declaration: {currency: {}},
                reinsuredDeclaration: {},
                adjustmentTitle: null,
                adjustmentDate: null,
                adjustmentDateInput: false,
                settlementDueDate: null,
                settlementDueDateInput: false,
                adjustmentAmount: "",
                reinsuredAdjustmentAmount: "",
                adjustmentLines: [],
                reinsuredAdjustmentLines: [],
                editMidTermAdjustmentData: {},
                commentary: "",
                submitBtnText: 'Submit',
                isButtonDisabled: false,
                uploadedDocumentsData: [],
                midTermAdjustment: {},
                isUpdating: false,
                documentsLoading: false,
                approvingInsurerIds: [],
                mtaTableHeaders: [
                    {
                        text: 'Insurer',
                        value: 'insurer_company.name'
                    },
                    {
                        text: 'Written line',
                        value: 'line_percent',
                        sortable: false,
                    },
                    {
                        text: 'Mid-Term Adjustment',
                        value: 'type',
                        hide_for_captive_line: true,
                        sortable: false,
                    },
                    {
                        text: 'Commentary',
                        value: 'price',
                        sortable: false,
                    }
                ],
                btnLoading: false,
                endorsementReference: null,
                endorsementReferenceExists: false,
            };
        },
        computed: {
            isCaptiveReinsurance() {
                return this.declaration.transaction_type === 'captive_reinsurance';
            },
            show: {
                get() {
                    return this.showDialog;
                },
                set(value) {
                    this.$emit("closeDialog");
                    return value;
                },
            },
            documentableId() {
                if (this.midTermAdjustment) {
                    return this.midTermAdjustment.id;
                }
            },
            riskHeader() {
                return {
                    id: this.declaration.risk_id,
                    client_company_name: this.declaration.client_company.name,
                    obligor_name: this.declaration.obligor_name,
                    country_name: this.declaration.country_id ? this.declaration.country.name : '',
                    status: this.declaration.status
                };
            },
            insurerList: function () {
                return this.declaration.lines.map(line => (line.captive_insurer_company_id ? line.firm_order_offer.captive_insurer_company : line.firm_order_offer.insurer_company))
            },
            adjustmentLinesList() {
                return this.adjustmentLines;
            },
            reinsuredAdjustmentLinesList() {
                return this.reinsuredAdjustmentLines;
            },
            percentageWritten() {
              return 100;
            },
            formattedWrittenLine() {
                const writtenLine = this.isCaptiveReinsurance ? this.declaration.current_contract_value : this.declaration.limit_of_liability

                return this.$options.filters.currency(parseFloat(writtenLine), this.declaration.currency.code);
            },
            paymentPremiumTerm() {
                let premiumPaymentTerm = this.declaration.premium_payment_term;

                if (this.isUpdating && this.midTermAdjustment) {
                    premiumPaymentTerm = this.midTermAdjustment.premium_payment_term;
                }

                return premiumPaymentTerm;
            },
            mtaDataList() {
                return [
                    {
                        text: 'Written line',
                        value: this.formattedWrittenLine
                    },
                    {
                        text: 'Premium payment term',
                        value: this.paymentPremiumTerm + ' days'
                    },
                ];
            },
            toggleSubmitBtnText() {
                return this.documentsLoading ? 'Processing' : this.submitBtnText;
            },
            nonCaptiveDeclarationLines() {
                return this.isCaptiveReinsurance
                        ? this.declaration?.lines.filter((line) => !line.captive_insurer_company_id) || [] : this.declaration?.lines || [];
            },
            captiveDeclarationLine() {
                return this.declaration.lines.find((line) => Boolean(line.captive_insurer_company_id));
            },
            hasDraftMTA() {
                return this.declaration.endorsements.some(endorsement => {
                    return endorsement.status === 'draft' && endorsement.type === 'mid_term_adjustment';
                })
            },
            numberOfInsurersSelected() {
                if (this.insurerList.length === this.approvingInsurerIds.length) {
                    return "All insurers";
                }
                return this.approvingInsurerIds.length === 1
                    ? "1 insurer"
                    : this.approvingInsurerIds.length + " insurers";
            }
        },
        async created() {
            await this.fetchData();

            if (!this.isUpdating) {
                this.hasDraftMTA ? this.fetchDraftMTA() : this.createDraftMTA();
            }
        },
        methods: {
            async fetchData() {
                const dataRequests = [];

                dataRequests.push(axios.get(this.$root.api(`broker/declaration/${this.$route.params.id}`)).then(res => {
                    this.declaration = res.data.data;
                }));

                await axios.all(dataRequests).then(() => {
                    if (this.$route.params.midTermAdjustmentId) {
                        const midTermAdjustmentId = Number(this.$route.params.midTermAdjustmentId);
                        this.midTermAdjustment = this.declaration.endorsements.find(endorsement => endorsement.id === midTermAdjustmentId);
                        this.isUpdating = true;

                        this.adjustmentLines = this.buildAdjustmentLines(this.isCaptiveReinsurance ? [this.captiveDeclarationLine] : this.nonCaptiveDeclarationLines);
                    }

                    if (this.isCaptiveReinsurance) {
                        this.reinsuredAdjustmentLines = this.buildAdjustmentLines(this.nonCaptiveDeclarationLines);
                    }

                    this.resetInsurers();

                    this.loading = false;
                });
            },
            createDraftMTA() {
                axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/mid-term-adjustment`)).then(response => {
                    this.midTermAdjustment = response.data.data;
                    this.adjustmentLines = this.buildAdjustmentLines(this.isCaptiveReinsurance ? [this.captiveDeclarationLine] : this.nonCaptiveDeclarationLines);
                });
            },
            fetchDraftMTA() {
                axios.get(this.$root.api(`broker/declaration/${this.declaration.id}/mid-term-adjustment`)).then(response => {
                    this.midTermAdjustment = response.data.data;

                    this.adjustmentDate = this.$options.filters.formatDate(this.midTermAdjustment.effective_date, undefined, 'YYYY-MM-DD');
                    this.settlementDueDate = this.$options.filters.formatDate(this.midTermAdjustment.settlement_due_date, undefined, 'YYYY-MM-DD');
                    this.adjustmentTitle = this.midTermAdjustment.title;
                    this.endorsementReference = this.midTermAdjustment.reference;

                    this.adjustmentLines = this.buildAdjustmentLines(this.isCaptiveReinsurance ? [this.captiveDeclarationLine] : this.nonCaptiveDeclarationLines);
                });
            },
            isPricingVertical() {
                return this.declaration.pricing_structure === "vertical";
            },

            isPricingHorizontal() {
                return this.declaration.pricing_structure === "horizontal";
            },
            getInsurerCompanyName(line) {
                if (line.captive_insurer_company_id) {
                    return line.captive_insurer_company.name;
                }

                return line.insurer_company.name;
            },
            resetInsurers() {
                if (this.isUpdating) {
                    this.approvingInsurerIds = this.midTermAdjustment.endorsement_requests
                        .filter(({status}) => status !== 'not_required')
                        .map(({insurer_company_id}) => insurer_company_id);
                } else {
                    this.approvingInsurerIds = this.insurerList.map(insurer => insurer.id);
                }
            },
            selectAllInsurers() {
                this.approvingInsurerIds = this.insurerList.map(insurer => insurer.id);
            },
            deselectAllInsurers() {
                this.approvingInsurerIds = [];
            },
            buildAdjustmentLines(lines, disabledAll = false) {
                let adjustmentLines = [];
                let counter = 0;

                if (this.isUpdating && this.midTermAdjustment) {
                    this.adjustmentDate = this.$options.filters.formatDate(this.midTermAdjustment.effective_date, undefined, 'YYYY-MM-DD');
                    this.settlementDueDate = this.$options.filters.formatDate(this.midTermAdjustment.settlement_due_date, undefined, 'YYYY-MM-DD');
                    this.adjustmentTitle = this.midTermAdjustment.title;
                    this.endorsementReference = this.midTermAdjustment.reference;
                }

                lines.forEach(
                    (line) => {
                        let adjustment = null;
                        counter++;

                        const insurerCompany = line.captive_insurer_company ? line.captive_insurer_company : line.insurer_company;
                        if (this.midTermAdjustment) {
                            adjustment = (this.midTermAdjustment.line_adjustments || []).find(lineAdjustment => lineAdjustment.line_id === line.id) ||
                                this.midTermAdjustment.endorsement_requests.find(endorsementRequest => endorsementRequest.insurer_company_id === insurerCompany.id);
                        }

                        const securityName = line.security ? line.security.name : null;

                        const adjLine = {
                            id: line.id,
                            line_amount: line.line_amount,
                            line_percent: line.line_percent,
                            insurer_company_name: securityName ? `${insurerCompany.name} — ${securityName}` : insurerCompany.name,
                            insurer_company_id: insurerCompany.id,
                            adjustment_amount: adjustment ? adjustment.adjustment_amount : null,
                            commentary: adjustment ? adjustment.commentary : null,
                        };

                        adjustmentLines.push(adjLine)
                    }
                );

                return adjustmentLines;
            },
            documentChanges(documentChanges) {
                if (documentChanges.isProcessing) {
                    this.submitBtnText = 'Processing';
                    this.isButtonDisabled = true;
                } else {
                    this.submitBtnText = this.isUpdating ? 'Update' : 'Submit';
                    this.isButtonDisabled = false;
                }

                this.uploadedDocumentsData = documentChanges.selectedDocumentData;
            },
            async save(midTermAdjustmentId, data) {
                if (midTermAdjustmentId) {
                    await axios.put(
                        this.$root.api(
                            `broker/declaration/${this.declaration.id}/mid-term-adjustment/${midTermAdjustmentId}`
                        ),
                        data
                    );
                } else {
                    await axios.post(
                        this.$root.api(
                            `broker/declaration/${this.declaration.id}/mid-term-adjustment/${this.midTermAdjustment.id}/submit`
                        ),
                        data
                    );
                }
            },
            async submit() {
                await this.checkIsReferenceUnique();

                if (this.endorsementReferenceExists) {
                    return;
                }

                if (this.$refs.midtermAdjustmentForm.validate()) {
                    if ((await this.$refs.confirmation.open()) === false) {
                        this.resetInsurers();
                        return;
                    }

                    const midTermAdjustmentId = this.isUpdating && this.midTermAdjustment ? this.midTermAdjustment.id : null;
                    this.loading = true;

                    const data = {
                        adjustment_date: this.adjustmentDate,
                        settlement_due_date: this.settlementDueDate,
                        adjustment_title: this.adjustmentTitle,
                        reference: this.endorsementReference,
                        lines: [...this.adjustmentLines, ...this.reinsuredAdjustmentLines],
                        commentary: this.commentary,
                        uploaded_documents: this.uploadedDocumentsData,
                        approving_insurer_ids: this.approvingInsurerIds,
                    };
                    this.save(midTermAdjustmentId, data).then(() => {
                        this.loading = false;
                        this.goToDeclaration();
                    });
                }
            },
            async saveChanges() {
                if (this.endorsementReference) {
                    await this.checkIsReferenceUnique();
                }

                if (this.endorsementReferenceExists) {
                    return;
                }

                this.btnLoading = true;
                const data = {
                    adjustment_date: this.adjustmentDate,
                    settlement_due_date: this.settlementDueDate,
                    adjustment_title: this.adjustmentTitle,
                    reference: this.endorsementReference,
                    lines: [...this.adjustmentLines, ...this.reinsuredAdjustmentLines],
                    commentary: this.commentary,
                };

                await axios.put(
                    this.$root.api(
                        `broker/declaration/${this.declaration.id}/mid-term-adjustment/${this.midTermAdjustment.id}/save-changes`
                    ),
                    data
                ).then(() => {
                    this.btnLoading = false
                });
            },
            goToDeclaration() {
                this.$router.push({path: `/agent/risk/${this.$route.params.id}`});
            },
            getEndLabelColor(percentage) {
                if (percentage === 100) {
                    return 'secondary';
                } else if (percentage > 100) {
                    return 'error'
                }

                return 'amber';
            },
            async checkIsReferenceUnique() {
                if (this.endorsementReference === null || this.endorsementReference === undefined || this.endorsementReference.trim().length === 0) {
                    this.endorsementReferenceExists = false;
                    return;
                }

                await axios.get(this.$root.api(`broker/amendment/check-reference`), {
                    params: {
                        declaration_id: this.declaration.id,
                        entity_reference: this.endorsementReference,
                        current_endorsement_id: this.midTermAdjustment.id
                    }
                }).then(response => {
                    this.endorsementReferenceExists = response.data.exists;
                });
                if (this.endorsementReferenceExists) {
                    if (await this.$refs.notUniqueEndorsementReference.open() === false) {
                        return;
                    }
                    window.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth'
                    });
                }
            },
        },
    };
</script>
